import * as React from 'react';
import { useEffect, useState } from 'react';
import { Button, ButtonGroup, Content, Dialog, Divider, Footer, Header, Heading, ProgressBar, SearchField, Text, ToastQueue, View } from '@adobe/react-spectrum';
import { useTranslation } from 'react-i18next';
import { FinderContextTree } from '../../../pages/protected/finder/components/FinderContextTree';
import { useFinderContextTree } from '../../../pages/protected/finder/components/FinderContextTree.hooks';
import Add from '@spectrum-icons/workflow/Add';
import { Key, Selection } from '@adobe/react-spectrum';
import { FPAData, FPADataTypes } from '../../../infra/protected/FPA/FPAData';
import { FPAItem } from '../../../infra/protected/FPA/FPAItem';
import { invokeFinderApp } from '../../../utils/NavigationUtils';
export interface IContextMoveDialogProps {
    onClose: (move: boolean) => void;
    parentItem: FPAData | null;
    selectedItem: FPAData | null;
    showActivities: boolean;
    showProjects: boolean;
    showFolders: boolean;
    selectedItemType: FPADataTypes;
    relatedToActivityType?: number;
    fpaTypeCategories?: string;
}

export function ContextMoveDialog ({ onClose, parentItem, selectedItem, showActivities, showProjects, showFolders, relatedToActivityType, fpaTypeCategories, selectedItemType }: IContextMoveDialogProps) {
    const { t } = useTranslation();
    const _t = (key: string, params: any = {}) => t(`contextMoveDialog.${key}`, { ns: 'finder', ...params }) as string;

    const [expandedKeys, setExpandedKeys] = React.useState<Set<Key>>(new Set());
    const [selectedKeys, setSelectedKeys] = React.useState<Selection>(new Set());
    const [searchValue, setSearchValue] = React.useState('');
    const [searchLabel, setSearchLabel] = useState(_t('search_label', { item: _t('top_level')}));
    const [allowMove, setAllowMove] = React.useState(false);
    const [moveItem, setMoveItem] = React.useState<FPAItem | null>(null);

    const { treeData, onSelection, loading, processMove, onSearch } = useFinderContextTree({ 
        parentItem: parentItem ,
        showFolders: showFolders,   
        showProjects: showProjects,
        showActivities: showActivities,
        additionalFilter: {
            relatedToActivityType: relatedToActivityType,
            fpaTypeCategories: fpaTypeCategories
        }
    });

    const haveItem = (list: FPAItem[], key: string): FPAItem | undefined => {
        var found = list.find(item => +item.id === +key);
        if(!found) {
            for (const item of list) {
                if(item.childItems) {
                    found = haveItem(item.childItems, key);
                }
                if(found) break;
            }
        }
        return found;
    }

    const handleSelection = (key: string) => {
        const item = haveItem(treeData, key);
        if(item) {
            setAllowMove(item.type === selectedItemType);
            setMoveItem(item);
            setSearchLabel(_t('search_label', { item: item.title }));
        }
        onSelection(Number(key));
    };

    const handleMove = async () => {
        if(moveItem && selectedItem) {
            setAllowMove(false);
            var move_result = await processMove(selectedItem.type, ''+selectedItem.id, ''+moveItem.id);
            if(move_result.moved) {
                onClose(true);
                ToastQueue.positive(_t('toast.move_success', { item: moveItem.title, context: moveItem.type }), { timeout: 3000,
                    actionLabel: _t('toast.open_item_label', { item: moveItem.title, context: moveItem.type }),
                    shouldCloseOnAction: true,
                    onAction: () => invokeFinderApp(move_result.itemId + '', selectedItem.type === FPADataTypes.FOLDER ? 'FOLDER' : selectedItem.type === FPADataTypes.PROJECT ? 'PROJECT' : 'ACTIVITY'),              
                 });
            } else {
                setAllowMove(true);
            }
        }
    }

    const handleSearch = (value: string) => {
        setSearchValue(value);
        onSearch(value, moveItem);
    }

    return (
        <div>
            <Dialog>
                <Heading>{_t('move_context')}</Heading>
                <Header>
                    <Button variant='primary' onPress={() => onClose(false)}>
                        <Add />
                        <Text>{_t('add_new_context')}</Text>
                    </Button>
                </Header>
                <Divider />
                <Content>
                    <View>
                        <SearchField
                            label={searchLabel}
                            onChange={handleSearch}
                            value={searchValue}
                            width='100%'
                        />
                    </View>
                    <FinderContextTree
                        items={treeData}
                        onSelection={handleSelection}
                        expandedKeys={expandedKeys}
                        setExpandedKeys={setExpandedKeys}
                        selectedKeys={selectedKeys}
                        setSelectedKeys={setSelectedKeys}
                    />
                </Content>
                <Footer>
                    <View>
                        {loading && (<ProgressBar label={_t('loading')+'...'} isIndeterminate />)}
                    </View>
                </Footer>
                <ButtonGroup>
                    <Button variant='secondary' onPress={() => onClose(false)}>{_t('cancel')}</Button>
                    <Button variant='accent' onPress={() => handleMove()} isDisabled={!allowMove}>{_t('move')}</Button>
                </ButtonGroup>
            </Dialog>
        </div>
    );
}
